/**
 * Pacientes - Sistema de Gestión de Quirófanos Centinela Salud
 * Enhanced UX/UI
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { pacientesAPI } from '../services/api'
import { useAuthStore, ROLES } from '../store'
import {
  Search, Plus, Eye, Edit, Trash2, Users, Building2, Clock,
  AlertTriangle, X, FileSpreadsheet, RefreshCw, Filter, Phone, Calendar, Shield
} from 'lucide-react'
import toast from 'react-hot-toast'

// Datos demo mejorados con hospital_id
const DEMO_PACIENTES = [
  { id: 1, nss: '1234567890', nombre: 'Maria Garcia Lopez', edad: 45, hospital_id: 1, hospital_nombre: 'HGZ No. 53 Los Reyes', estado: 'en_espera', prioridad: 'alta', procedimiento: 'Colecistectomia', dias_espera: 45, telefono: '555-123-4567' },
  { id: 2, nss: '2345678901', nombre: 'Jose Hernandez Martinez', edad: 62, hospital_id: 2, hospital_nombre: 'HGZ No. 57 La Quebrada', estado: 'activo', prioridad: 'media', procedimiento: 'Hernioplastia', dias_espera: 23, telefono: '555-234-5678' },
  { id: 3, nss: '3456789012', nombre: 'Ana Rodriguez Sanchez', edad: 38, hospital_id: 3, hospital_nombre: 'HGZ No. 68 Tulpetlac', estado: 'en_espera', prioridad: 'alta', procedimiento: 'Apendicectomia', dias_espera: 92, telefono: '555-345-6789' },
  { id: 4, nss: '4567890123', nombre: 'Carlos Lopez Torres', edad: 55, hospital_id: 4, hospital_nombre: 'HGZ No. 76 Xalostoc', estado: 'programado', prioridad: 'baja', procedimiento: 'Artroscopia', dias_espera: 15, telefono: '555-456-7890' },
  { id: 5, nss: '5678901234', nombre: 'Laura Martinez Flores', edad: 29, hospital_id: 5, hospital_nombre: 'HGR No. 196 Ecatepec', estado: 'activo', prioridad: 'media', procedimiento: 'Cesarea', dias_espera: 8, telefono: '555-567-8901' },
  { id: 6, nss: '6789012345', nombre: 'Pedro Gonzalez Ramirez', edad: 71, hospital_id: 6, hospital_nombre: 'HGZ No. 197 Texcoco', estado: 'en_espera', prioridad: 'alta', procedimiento: 'Prostatectomia', dias_espera: 120, telefono: '555-678-9012' },
  { id: 7, nss: '7890123456', nombre: 'Sofia Sanchez Morales', edad: 42, hospital_id: 1, hospital_nombre: 'HGZ No. 53 Los Reyes', estado: 'activo', prioridad: 'media', procedimiento: 'Tiroidectomia', dias_espera: 34, telefono: '555-789-0123' },
  { id: 8, nss: '8901234567', nombre: 'Miguel Torres Diaz', edad: 58, hospital_id: 2, hospital_nombre: 'HGZ No. 57 La Quebrada', estado: 'programado', prioridad: 'baja', procedimiento: 'Mastectomia', dias_espera: 67, telefono: '555-890-1234' },
  { id: 9, nss: '9012345678', nombre: 'Rosa Mendez Vega', edad: 48, hospital_id: 3, hospital_nombre: 'HGZ No. 68 Tulpetlac', estado: 'en_espera', prioridad: 'alta', procedimiento: 'Colecistectomia', dias_espera: 78, telefono: '555-901-2345' },
  { id: 10, nss: '0123456789', nombre: 'Fernando Ruiz Castro', edad: 65, hospital_id: 4, hospital_nombre: 'HGZ No. 76 Xalostoc', estado: 'activo', prioridad: 'media', procedimiento: 'Hernioplastia', dias_espera: 41, telefono: '555-012-3456' },
]

// Animated Counter
const AnimatedCounter = ({ value, duration = 800 }) => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let start = 0
    const end = parseInt(value)
    if (start === end) return
    const timer = setInterval(() => {
      start += Math.ceil(end / (duration / 50))
      if (start >= end) {
        setCount(end)
        clearInterval(timer)
      } else {
        setCount(start)
      }
    }, 50)
    return () => clearInterval(timer)
  }, [value, duration])
  return <span>{count}</span>
}

// KPI Card
const KPICard = ({ title, value, subtitle, icon: IconComponent, color, onClick, active }) => {
  const gradients = {
    emerald: 'from-emerald-500 via-emerald-600 to-teal-600',
    amber: 'from-amber-500 via-orange-500 to-orange-600',
    blue: 'from-blue-500 via-blue-600 to-indigo-600',
    red: 'from-red-500 via-red-600 to-rose-600',
  }

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br ${gradients[color]} shadow-lg text-white cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${active ? 'ring-4 ring-white/50' : ''}`}
    >
      <div className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors duration-300" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-500" />
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-4">
          <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
            <IconComponent className="w-5 h-5" />
          </div>
        </div>
        <div className="text-4xl font-bold mb-1 tracking-tight">
          <AnimatedCounter value={value} />
        </div>
        <div className="text-sm font-semibold opacity-95">{title}</div>
        {subtitle && <div className="text-xs opacity-75 mt-1">{subtitle}</div>}
      </div>
    </div>
  )
}

// Patient Modal
const PatientModal = ({ patient, onClose }) => {
  if (!patient) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-slideUp">
        <div className={`p-6 text-white ${
          patient.prioridad === 'alta' ? 'bg-gradient-to-r from-red-500 to-rose-600' :
          patient.prioridad === 'media' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
          'bg-gradient-to-r from-emerald-500 to-teal-600'
        }`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{patient.nombre}</h2>
              <p className="text-white/80 text-sm mt-1">NSS: {patient.nss}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Edad</p>
              <p className="text-xl font-bold text-gray-800 mt-1">{patient.edad} años</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Dias en Espera</p>
              <p className={`text-xl font-bold ${patient.dias_espera > 90 ? 'text-red-600' : patient.dias_espera > 60 ? 'text-amber-600' : 'text-green-600'}`}>{patient.dias_espera}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Procedimiento</p>
              <p className="font-semibold text-gray-800 mt-1">{patient.procedimiento}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Estado</p>
              <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                patient.estado === 'activo' ? 'bg-green-100 text-green-700' :
                patient.estado === 'en_espera' ? 'bg-amber-100 text-amber-700' :
                'bg-blue-100 text-blue-700'
              }`}>{patient.estado?.replace('_', ' ')}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Hospital Asignado</p>
            <p className="font-semibold text-gray-800 mt-1 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" />
              {patient.hospital_nombre}
            </p>
          </div>
          <div className="flex gap-3 pt-2">
            <button className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors">
              <Calendar className="w-4 h-4" />
              Programar Cirugia
            </button>
            <button className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors">
              <Phone className="w-4 h-4" />
              Llamar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Pacientes() {
  // Auth context
  const user = useAuthStore(state => state.user)
  const isAdmin = user?.rol === ROLES.ADMIN
  const isOperador = user?.rol === ROLES.OPERADOR
  const userHospitalId = user?.hospital_id

  const [pacientes, setPacientes] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterEstado, setFilterEstado] = useState('')
  const [filterPrioridad, setFilterPrioridad] = useState('')
  const [selectedPatient, setSelectedPatient] = useState(null)

  useEffect(() => {
    loadPacientes()
  }, [])

  const loadPacientes = async () => {
    try {
      const response = await pacientesAPI.getAll()
      let data = response.data?.data || response.data || []
      // Filtrar por hospital si es operador
      if ((isOperador || user?.rol === ROLES.HOSPITAL_USER) && userHospitalId) {
        data = data.filter(p => p.hospital_id === userHospitalId)
      }
      setPacientes(data)
    } catch (error) {
      console.error('Error loading pacientes, usando datos demo:', error)
      let demoData = DEMO_PACIENTES
      // Filtrar datos demo por hospital si es operador
      if ((isOperador || user?.rol === ROLES.HOSPITAL_USER) && userHospitalId) {
        demoData = demoData.filter(p => p.hospital_id === userHospitalId)
      }
      setPacientes(demoData)
    } finally {
      setLoading(false)
    }
  }

  const filteredPacientes = useMemo(() => {
    return pacientes.filter(p => {
      const matchSearch = !search ||
        p.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        p.nss?.includes(search)
      const matchEstado = !filterEstado || p.estado === filterEstado
      const matchPrioridad = !filterPrioridad || p.prioridad === filterPrioridad
      return matchSearch && matchEstado && matchPrioridad
    })
  }, [pacientes, search, filterEstado, filterPrioridad])

  const stats = useMemo(() => ({
    total: pacientes.length,
    enEspera: pacientes.filter(p => p.estado === 'en_espera').length,
    activos: pacientes.filter(p => p.estado === 'activo').length,
    prioridadAlta: pacientes.filter(p => p.prioridad === 'alta').length,
  }), [pacientes])

  const handleExport = useCallback(() => {
    const csvContent = [
      ['NSS', 'Nombre', 'Edad', 'Hospital', 'Estado', 'Prioridad', 'Procedimiento', 'Dias Espera'].join(','),
      ...filteredPacientes.map(p =>
        [p.nss, p.nombre, p.edad, p.hospital_nombre, p.estado, p.prioridad, p.procedimiento, p.dias_espera].join(',')
      )
    ].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pacientes_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success('Datos exportados correctamente')
  }, [filteredPacientes])

  const handleResetFilters = () => {
    setSearch('')
    setFilterEstado('')
    setFilterPrioridad('')
  }

  const getPrioridadColor = (prioridad) => {
    const colors = { alta: 'bg-red-500', media: 'bg-amber-500', baja: 'bg-emerald-500' }
    return colors[prioridad] || 'bg-gray-500'
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-emerald-200 rounded-full animate-spin border-t-emerald-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Users className="w-6 h-6 text-emerald-600 animate-pulse" />
          </div>
        </div>
        <p className="mt-4 text-gray-500 font-medium">Cargando pacientes...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-pageEnter">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Pacientes</h1>
            {(isOperador || user?.rol === ROLES.HOSPITAL_USER) && user?.hospital_nombre && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {user.hospital_nombre}
              </span>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {(isOperador || user?.rol === ROLES.HOSPITAL_USER)
              ? `${pacientes.length} pacientes de tu unidad medica`
              : `${pacientes.length} pacientes registrados en el sistema`
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-green-50 border border-green-200 mr-2">
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-xs text-green-600 font-medium">Modo Administrador</p>
                <p className="text-sm font-semibold text-green-800">Todos los hospitales</p>
              </div>
            </div>
          )}
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-all shadow-sm"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Exportar
          </button>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-all shadow-sm">
            <Plus className="w-4 h-4" />
            Nuevo Paciente
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Pacientes"
          value={stats.total}
          subtitle="Registrados"
          icon={Users}
          color="emerald"
          onClick={() => handleResetFilters()}
          active={!filterEstado && !filterPrioridad}
        />
        <KPICard
          title="En Espera"
          value={stats.enEspera}
          subtitle="Pendientes de cirugia"
          icon={Clock}
          color="amber"
          onClick={() => { setFilterEstado('en_espera'); setFilterPrioridad(''); }}
          active={filterEstado === 'en_espera'}
        />
        <KPICard
          title="Activos"
          value={stats.activos}
          subtitle="En tratamiento"
          icon={Users}
          color="blue"
          onClick={() => { setFilterEstado('activo'); setFilterPrioridad(''); }}
          active={filterEstado === 'activo'}
        />
        <KPICard
          title="Prioridad Alta"
          value={stats.prioridadAlta}
          subtitle="Requieren atencion"
          icon={AlertTriangle}
          color="red"
          onClick={() => { setFilterPrioridad('alta'); setFilterEstado(''); }}
          active={filterPrioridad === 'alta'}
        />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[250px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Nombre o NSS..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all"
              />
            </div>
          </div>
          <div className="min-w-[180px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Estado</label>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all appearance-none cursor-pointer"
            >
              <option value="">Todos</option>
              <option value="en_espera">En Espera</option>
              <option value="activo">Activo</option>
              <option value="programado">Programado</option>
            </select>
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prioridad</label>
            <select
              value={filterPrioridad}
              onChange={(e) => setFilterPrioridad(e.target.value)}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all appearance-none cursor-pointer"
            >
              <option value="">Todas</option>
              <option value="alta">Alta</option>
              <option value="media">Media</option>
              <option value="baja">Baja</option>
            </select>
          </div>
          {(search || filterEstado || filterPrioridad) && (
            <button
              onClick={handleResetFilters}
              className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-all flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Lista de Pacientes</h3>
                <p className="text-xs text-gray-500">{filteredPacientes.length} resultados</p>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Paciente</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">NSS</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Procedimiento</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Hospital</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Espera</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPacientes.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-16 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Users className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">No se encontraron pacientes</p>
                    <p className="text-gray-400 text-sm mt-1">Ajusta los filtros para ver resultados</p>
                  </td>
                </tr>
              ) : (
                filteredPacientes.map((paciente) => (
                  <tr key={paciente.id} className="hover:bg-gray-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shadow-sm ${getPrioridadColor(paciente.prioridad)}`}>
                          {paciente.nombre?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{paciente.nombre}</p>
                          <p className="text-xs text-gray-500">{paciente.edad} años</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">{paciente.nss}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{paciente.procedimiento}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        {paciente.hospital_nombre}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                        paciente.estado === 'activo' ? 'bg-green-100 text-green-700' :
                        paciente.estado === 'en_espera' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {paciente.estado?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className={`w-4 h-4 ${paciente.dias_espera > 90 ? 'text-red-500' : paciente.dias_espera > 60 ? 'text-amber-500' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${paciente.dias_espera > 90 ? 'text-red-600' : paciente.dias_espera > 60 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {paciente.dias_espera} dias
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setSelectedPatient(paciente)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Ver detalles"
                        >
                          <Eye size={18} />
                        </button>
                        <button className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Editar">
                          <Edit size={18} />
                        </button>
                        <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {selectedPatient && (
        <PatientModal patient={selectedPatient} onClose={() => setSelectedPatient(null)} />
      )}

      {/* Estilos */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>
    </div>
  )
}
